/**
 * I passi del tutorial guidato dell'elenco Packing list.
 *
 * Due preparativi: il menu del piu' in alto e il menu della riga.
 * `stopPackingListsTour` rimette tutto com'era.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="packing-lists"] [data-tour="${name}"]`;

const ROW_OPEN = '[data-tour="row-open"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenus() {
	await afterPendingClose();

	// L'attivatore fa da interruttore: il secondo click richiude.
	for (const [activator, probe] of [
		[at('row-menu'), ROW_OPEN],
	]) {
		if (isVisible(probe)) {
			click(activator);
			await waitFor(probe, false);
		}
	}
}

const openRowMenu = () => openMenu(at('row-menu'), ROW_OPEN);


/** Rimette la pagina com'era: menu chiusi. */
export async function stopPackingListsTour() {
	await closeMenus();

}

export function createPackingListsTourSteps() {
	return [
		{
			body: "Una packing list e' la preparazione fisica di un ordine: si prende l'ordine, si passano i prodotti sotto il lettore di barcode uno per uno, e quando ci sono tutti la si chiude. Serve a non sbagliare spedizione - e a lasciare traccia di chi ha preparato cosa e quando. Questa pagina e' l'archivio di tutte le preparazioni, aperte e chiuse.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "La ricerca guarda solo il numero dell'ordine, e parte da tre caratteri: e' il modo per ritrovare la preparazione di un ordine preciso quando un cliente chiama.",
			before: closeMenus,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare una packing list',
		},
		{
			body: "Data ordine filtra per intervallo - una data sola per avere tutto da li' in avanti, due per un periodo chiuso - e Status separa le preparazioni ancora aperte da quelle concluse. Il filtro sullo stato e' quello che si usa di piu': 'In elaborazione' e' la lista di quello che e' rimasto a meta'.",
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'I filtri',
		},
		{
			body: "Ogni riga e' una preparazione. Data Inizio e' quando qualcuno l'ha aperta, Data Fine quando l'ha chiusa - se manca, e' ancora aperta. Lo Status e' a colori e si legge al volo: verde Completata, rosso In elaborazione. Ci sono poi la piattaforma da cui arriva l'ordine con la bandiera del paese di consegna, il cliente, e l'eventuale coupon generato alla chiusura.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "La lente accanto al numero d'ordine non apre la packing list: apre l'ordine. Serve quando, mentre prepari, ti serve controllare qualcosa che nella packing list non c'e' - una nota del cliente, il pagamento, l'indirizzo per esteso.",
			key: 'order-link',
			placement: 'right',
			target: at('order-link'),
			timeout: 250,
			title: "Andare all'ordine",
		},
		{
			body: "I tre pallini hanno una voce sola, Apri dettaglio, ed e' li' che si lavora: la pagina della packing list con la spunta dei prodotti col lettore, il corriere, il tracking e la chiusura. Quella pagina ha un tutorial suo.",
			before: openRowMenu,
			key: 'row-open',
			placement: 'right',
			target: ROW_OPEN,
			timeout: 250,
			title: 'Aprire il dettaglio',
		},
		{
			body: "Il piu' apre la pagina di avvio: si identifica l'operatore col badge, si sceglie l'ordine da preparare e si comincia. Anche quella pagina ha il suo tutorial.",
			key: 'add-new',
			placement: 'left',
			target: at('add'),
			title: 'Cominciarne una nuova',
		},
	];
}
